import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Container} from 'src/design/atoms/Container';
import {Loader} from 'src/design/atoms/Loader';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';

import type {SignInInput} from 'aws-amplify/auth';
import {useEffect, useState} from 'react';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';

import {IconCheck} from '@tabler/icons-react';
import {useResetToPlatformClient} from 'src/app/security/authentication/useResetToPlatformClient';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {resetAuthState, signInUser} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {InvitationExistingUserMessage} from './components/InvitationExistingUserMessage';
import {InvitationUserCheck} from './components/InvitationUserCheck';
import classes from './Invitation.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type InvitationState =
  | 'pending'
  | 'checking'
  | 'loading'
  | 'error'
  | 'user-exists';

const InvitationPage = () => {
  useSetDocumentTitle('Account Invitation');

  const [searchParams] = useSearchParams();
  const username = searchParams.get('user')?.replace(' ', '+');
  const token = searchParams.get('token');
  const [state, setState] = useState<InvitationState>('pending');

  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const {message, isSignedIn, signInNextStep} = useAppSelector(
    state => state.auth
  );

  useResetToPlatformClient();

  const handleUserValidated = (canProceed: boolean) => {
    if (canProceed && username && token) {
      setState('loading');
      const input = {
        username: username,
        password: token,
      } as SignInInput;

      void dispatch(signInUser(input));
    } else {
      setState('user-exists');
    }
  };

  const handleContinue = () => {
    if (username && token) {
      setState('checking');
    }
  };

  // Check if we have the required parameters
  useEffect(() => {
    if (!username || !token) {
      setState('error');
    }
  }, [username, token]);

  // Reset auth state when parameters change
  useEffect(() => {
    if (username && token) {
      dispatch(resetAuthState());
    }
  }, [username, token, dispatch]);

  useEffect(() => {
    if (signInNextStep && state === 'loading') {
      navigate(PathUtility.Login(), {replace: true});
    }
  }, [isSignedIn, signInNextStep, state, navigate]);

  return (
    <Container size={450} my={40}>
      <Title ta="center" className={classes.title}>
        Account Setup
      </Title>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        {state === 'pending' && (
          <Stack>
            <Alert color="green" title="Welcome back!" icon={<IconCheck />}>
              Click the button below to continue your account setup.
            </Alert>
            <Button
              onClick={handleContinue}
              aria-label="continue with invitation button">
              Continue setup
            </Button>
          </Stack>
        )}
        {state === 'checking' && username && token && (
          <InvitationUserCheck
            username={username}
            token={token}
            onUserValidated={handleUserValidated}
          />
        )}
        {state === 'loading' && (
          <Center>
            <Loader size="sm" mr="md" />
            <Text>Finishing account setup...</Text>
          </Center>
        )}
        {state === 'user-exists' && username && (
          <InvitationExistingUserMessage username={username} />
        )}
        {state === 'error' && (
          <ServerErrorAlert
            message={
              'Invalid invitation link. Please contact your administrator for a new invitation.'
            }
          />
        )}
        <ServerErrorAlert message={message} />
      </Paper>
    </Container>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.Invitation(),
  element: <InvitationPage />,
  isPrivate: false,
});

export {InvitationPage};
